import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Calculator, ArrowRight, CheckCircle, Clock, Zap } from 'lucide-react';

interface CalculatorResult {
  minPrice: number;
  maxPrice: number;
  timeline: string;
  features: string[];
}

const WebsiteCostCalculator = () => {
  const [pages, setPages] = useState([5]);
  const [languages, setLanguages] = useState<string>('');
  const [cms, setCms] = useState<string>('');
  const [designTier, setDesignTier] = useState<string>('');
  const [timeline, setTimeline] = useState<string>('');
  const [result, setResult] = useState<CalculatorResult | null>(null);
  const [showForm, setShowForm] = useState(false);

  // GA4 Tracking
  useEffect(() => {
    // Track calculator view
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'calculator_view', {
        event_category: 'engagement',
        event_label: 'website_cost_calculator'
      });
    }
  }, []);

  const calculateCost = () => {
    let basePrice = 2500; // Base CHF price
    let multiplier = 1;
    let timelineResult = '2-3 Wochen';
    const features: string[] = [];

    // Pages calculation
    const pageCount = pages[0];
    if (pageCount <= 5) {
      basePrice = 3500;
      features.push('Bis zu 5 Seiten');
    } else if (pageCount <= 10) {
      basePrice = 5500;
      multiplier = 1.2;
      features.push('Bis zu 10 Seiten');
    } else if (pageCount <= 20) {
      basePrice = 8500;
      multiplier = 1.4;
      features.push('Bis zu 20 Seiten');
      timelineResult = '3-4 Wochen';
    } else {
      basePrice = 12500;
      multiplier = 1.6;
      features.push('20+ Seiten');
      timelineResult = '4-6 Wochen';
    }

    // Languages multiplier
    switch (languages) {
      case 'de':
        features.push('Deutsch');
        break;
      case 'de-en':
        multiplier *= 1.3;
        features.push('Deutsch + English');
        break;
      case 'de-fr-it':
        multiplier *= 1.6;
        features.push('Deutsch + Français + Italiano');
        timelineResult = '4-5 Wochen';
        break;
      case 'all':
        multiplier *= 1.8;
        features.push('DE + FR + IT + EN');
        timelineResult = '5-6 Wochen';
        break;
    }

    // CMS complexity
    switch (cms) {
      case 'simple':
        features.push('Einfaches CMS');
        break;
      case 'advanced':
        multiplier *= 1.2;
        features.push('Advanced CMS');
        break;
      case 'ecommerce':
        multiplier *= 1.5;
        features.push('E-Commerce CMS');
        timelineResult = '4-6 Wochen';
        break;
      case 'custom':
        multiplier *= 2;
        features.push('Custom Development');
        timelineResult = '6-8 Wochen';
        break;
    }

    // Design tier
    switch (designTier) {
      case 'standard':
        features.push('Standard Design');
        break;
      case 'premium':
        multiplier *= 1.3;
        features.push('Premium Design');
        break;
      case 'luxury':
        multiplier *= 1.8;
        features.push('Luxury Design');
        timelineResult = '4-8 Wochen';
        break;
    }

    // Timeline adjustment
    switch (timeline) {
      case 'express':
        multiplier *= 1.5;
        features.push('Express-Lieferung');
        timelineResult = '1-2 Wochen';
        break;
      case 'standard':
        features.push('Standard Timeline');
        break;
      case 'flexible':
        multiplier *= 0.9;
        features.push('Flexible Timeline');
        break;
    }

    const finalPrice = Math.round(basePrice * multiplier);
    const minPrice = Math.round(finalPrice * 0.85);
    const maxPrice = Math.round(finalPrice * 1.15);

    setResult({
      minPrice,
      maxPrice,
      timeline: timelineResult,
      features
    });

    // GA4 Tracking for calculation
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'calculator_submit', {
        event_category: 'engagement',
        event_label: 'website_cost_calculator',
        value: finalPrice,
        custom_parameters: {
          pages: pageCount,
          languages,
          cms,
          design_tier: designTier,
          timeline
        }
      });
    }
  };

  const handleAuditRequest = () => {
    setShowForm(true);
    
    // GA4 Tracking for CTA click
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'lead_form_started', {
        event_category: 'conversion',
        event_label: 'website_audit_request',
        value: result ? (result.minPrice + result.maxPrice) / 2 : 0
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center mb-8">
        <Badge className="mb-4 bg-blue-600/20 text-blue-200 border-blue-500/30">
          <Calculator className="w-4 h-4 mr-2" />
          Kostenloses Tool
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4">
          Website Kosten Rechner Schweiz
        </h1>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">
          Berechnen Sie die Kosten für Ihre neue Website in der Schweiz. 
          Transparent, fair und auf den Schweizer Markt abgestimmt.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Calculator Inputs */}
        <Card className="bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Calculator className="w-5 h-5 mr-2 text-blue-400" />
              Website Konfiguration
            </CardTitle>
            <CardDescription className="text-gray-300">
              Passen Sie Ihre Website-Anforderungen an
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Number of Pages */}
            <div className="space-y-3">
              <Label className="text-white font-medium">
                Anzahl Seiten: {pages[0]}
              </Label>
              <Slider
                value={pages}
                onValueChange={setPages}
                max={50}
                min={3}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-400">
                <span>3 Seiten</span>
                <span>50+ Seiten</span>
              </div>
            </div>

            {/* Languages */}
            <div className="space-y-3">
              <Label className="text-white font-medium">Sprachen</Label>
              <Select value={languages} onValueChange={setLanguages}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Sprachauswahl" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="de">Nur Deutsch</SelectItem>
                  <SelectItem value="de-en">Deutsch + English</SelectItem>
                  <SelectItem value="de-fr-it">Deutsch + Français + Italiano</SelectItem>
                  <SelectItem value="all">Alle 4 Sprachen (DE/FR/IT/EN)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* CMS Type */}
            <div className="space-y-3">
              <Label className="text-white font-medium">CMS & Funktionen</Label>
              <Select value={cms} onValueChange={setCms}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Content Management System" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="simple">Einfaches CMS (WordPress/Webflow)</SelectItem>
                  <SelectItem value="advanced">Advanced CMS + Features</SelectItem>
                  <SelectItem value="ecommerce">E-Commerce Shop</SelectItem>
                  <SelectItem value="custom">Custom Development</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Design Tier */}
            <div className="space-y-3">
              <Label className="text-white font-medium">Design-Level</Label>
              <Select value={designTier} onValueChange={setDesignTier}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Design-Qualität" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="standard">Standard Business Design</SelectItem>
                  <SelectItem value="premium">Premium Corporate Design</SelectItem>
                  <SelectItem value="luxury">Luxury Brand Design</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Timeline */}
            <div className="space-y-3">
              <Label className="text-white font-medium">Timeline</Label>
              <Select value={timeline} onValueChange={setTimeline}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Gewünschte Lieferzeit" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="express">Express (1-2 Wochen) +50%</SelectItem>
                  <SelectItem value="standard">Standard (3-4 Wochen)</SelectItem>
                  <SelectItem value="flexible">Flexibel (5+ Wochen) -10%</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={calculateCost}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!languages || !cms || !designTier || !timeline}
            >
              <Calculator className="w-4 h-4 mr-2" />
              Kosten berechnen
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Zap className="w-5 h-5 mr-2 text-green-400" />
              Ihre Website-Kosten
            </CardTitle>
            <CardDescription className="text-gray-300">
              Transparente Schweizer Preise
            </CardDescription>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-6">
                {/* Price Range */}
                <div className="text-center p-6 bg-gradient-to-r from-blue-600/20 to-green-600/20 rounded-lg border border-blue-500/30">
                  <div className="text-3xl font-bold text-white mb-2">
                    CHF {result.minPrice.toLocaleString()} - {result.maxPrice.toLocaleString()}
                  </div>
                  <div className="text-blue-200 flex items-center justify-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Timeline: {result.timeline}
                  </div>
                </div>

                {/* Features Included */}
                <div className="space-y-3">
                  <h3 className="text-white font-semibold">Inklusive Features:</h3>
                  <div className="space-y-2">
                    {result.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-gray-300">
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                      Responsive Design (Mobile + Desktop)
                    </div>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                      SEO-Grundoptimierung
                    </div>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                      Google Analytics Setup
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3">
                  <Button 
                    onClick={handleAuditRequest}
                    className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Jetzt kostenloses Audit buchen
                  </Button>
                  
                  <div className="text-center text-sm text-gray-400">
                    ✓ 15-Minuten Beratung ✓ Konkrete Empfehlungen ✓ Unverbindlich
                  </div>
                </div>

                {/* Trust Indicators */}
                <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-gray-700">
                  <div>
                    <div className="text-2xl font-bold text-blue-400">89+</div>
                    <div className="text-xs text-gray-400">Websites</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-400">4.9/5</div>
                    <div className="text-xs text-gray-400">Bewertung</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-400">14 Tage</div>
                    <div className="text-xs text-gray-400">Ø Lieferzeit</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Wählen Sie Ihre Anforderungen links aus, um eine Kostenschätzung zu erhalten.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Form Modal/Section */}
      {showForm && (
        <Card className="bg-gradient-to-b from-green-800/50 to-green-900/50 border-green-700">
          <CardHeader>
            <CardTitle className="text-white">Kostenloses Website-Audit anfordern</CardTitle>
            <CardDescription className="text-green-200">
              Erhalten Sie eine detaillierte Analyse und konkrete Empfehlungen für Ihr Projekt.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-white">Name *</Label>
                  <Input className="bg-slate-800 border-slate-600 text-white" placeholder="Ihr Name" />
                </div>
                <div>
                  <Label className="text-white">E-Mail *</Label>
                  <Input className="bg-slate-800 border-slate-600 text-white" placeholder="ihre@email.ch" />
                </div>
                <div>
                  <Label className="text-white">Telefon</Label>
                  <Input className="bg-slate-800 border-slate-600 text-white" placeholder="+41 XX XXX XX XX" />
                </div>
                <div>
                  <Label className="text-white">Unternehmen</Label>
                  <Input className="bg-slate-800 border-slate-600 text-white" placeholder="Ihr Unternehmen" />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-white">Aktuelle Website (falls vorhanden)</Label>
                  <Input className="bg-slate-800 border-slate-600 text-white" placeholder="https://ihre-website.ch" />
                </div>
                <div>
                  <Label className="text-white">Projektbeschreibung</Label>
                  <textarea 
                    className="w-full p-3 bg-slate-800 border border-slate-600 text-white rounded-md"
                    rows={4}
                    placeholder="Beschreiben Sie kurz Ihr Website-Projekt..."
                  />
                </div>
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                  Audit-Termin buchen (15 Min)
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
  }
}

export default WebsiteCostCalculator;